import type { Annotation, PageRef, Point, Rect, Rotation } from '@/types'

/** PDF-style affine matrix [a, b, c, d, e, f]: x' = a*x + c*y + e, y' = b*x + d*y + f */
export type Matrix = [number, number, number, number, number, number]

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0]
/** CSS pixels per PDF point at 100% zoom (matches pdf.js / Acrobat "actual size"). */
export const PDF_TO_CSS = 96 / 72

export function applyMatrix(m: Matrix, x: number, y: number): Point {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] }
}

/** Returns the matrix that applies `first` and then `second`. */
export function compose(first: Matrix, second: Matrix): Matrix {
  const [a1, b1, c1, d1, e1, f1] = first
  const [a2, b2, c2, d2, e2, f2] = second
  return [
    a1 * a2 + b1 * c2,
    a1 * b2 + b1 * d2,
    c1 * a2 + d1 * c2,
    c1 * b2 + d1 * d2,
    e1 * a2 + f1 * c2 + e2,
    e1 * b2 + f1 * d2 + f2,
  ]
}

export function invert(m: Matrix): Matrix {
  const [a, b, c, d, e, f] = m
  const det = a * d - b * c
  if (det === 0) throw new Error('Matrix is not invertible')
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det]
}

/**
 * Same math as pdf.js PageViewport at scale 1: maps PDF user space to the page's
 * base space (intrinsic rotation applied, origin top-left, y down).
 */
export function viewportTransform(view: [number, number, number, number], rotation: number): Matrix {
  const [x1, y1, x2, y2] = view
  const cx = (x1 + x2) / 2
  const cy = (y1 + y2) / 2
  let A: number
  let B: number
  let C: number
  let D: number
  switch (((rotation % 360) + 360) % 360) {
    case 90:
      A = 0
      B = 1
      C = 1
      D = 0
      break
    case 180:
      A = -1
      B = 0
      C = 0
      D = 1
      break
    case 270:
      A = 0
      B = -1
      C = -1
      D = 0
      break
    default:
      A = 1
      B = 0
      C = 0
      D = -1
  }
  let ox: number
  let oy: number
  if (A === 0) {
    ox = Math.abs(cy - y1)
    oy = Math.abs(cx - x1)
  } else {
    ox = Math.abs(cx - x1)
    oy = Math.abs(cy - y1)
  }
  return [A, B, C, D, ox - A * cx - C * cy, oy - B * cx - D * cy]
}

/**
 * Matrix mapping "base-up" coordinates (base space with y flipped so y points up, which is
 * what pdf-lib's drawing helpers expect) to PDF user space.
 */
export function baseUpToUser(page: Pick<PageRef, 'view' | 'baseRotation' | 'height'>): Matrix {
  const flip: Matrix = [1, 0, 0, -1, 0, page.height]
  return compose(flip, invert(viewportTransform(page.view, page.baseRotation)))
}

export function baseToUser(page: Pick<PageRef, 'view' | 'baseRotation'>): Matrix {
  return invert(viewportTransform(page.view, page.baseRotation))
}

export function normRect(x1: number, y1: number, x2: number, y2: number): Rect {
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) }
}

export function rectsIntersect(a: Rect, b: Rect) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
}

export function pointInRect(p: Point, r: Rect, pad = 0) {
  return p.x >= r.x - pad && p.x <= r.x + r.w + pad && p.y >= r.y - pad && p.y <= r.y + r.h + pad
}

export function unionRects(rects: Rect[]): Rect {
  if (!rects.length) return { x: 0, y: 0, w: 0, h: 0 }
  let x1 = Number.POSITIVE_INFINITY
  let y1 = Number.POSITIVE_INFINITY
  let x2 = Number.NEGATIVE_INFINITY
  let y2 = Number.NEGATIVE_INFINITY
  for (const r of rects) {
    x1 = Math.min(x1, r.x)
    y1 = Math.min(y1, r.y)
    x2 = Math.max(x2, r.x + r.w)
    y2 = Math.max(y2, r.y + r.h)
  }
  return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 }
}

export const NOTE_SIZE = 22

/** Axis-aligned bounding box of an annotation in base space. */
export function annBBox(a: Annotation): Rect {
  switch (a.type) {
    case 'ink': {
      const half = a.width / 2
      const r = unionRects(a.points.map(([x, y]) => ({ x, y, w: 0, h: 0 })))
      return { x: r.x - half, y: r.y - half, w: r.w + a.width, h: r.h + a.width }
    }
    case 'rect':
    case 'ellipse':
    case 'text':
    case 'textEdit':
    case 'image':
      return { x: a.x, y: a.y, w: a.w, h: a.h }
    case 'line':
    case 'arrow': {
      const r = normRect(a.x1, a.y1, a.x2, a.y2)
      const p = a.strokeWidth * (a.type === 'arrow' ? 3 : 1)
      return { x: r.x - p, y: r.y - p, w: r.w + p * 2, h: r.h + p * 2 }
    }
    case 'highlight':
    case 'underline':
    case 'strikeout':
      return unionRects(a.rects)
    case 'note':
      return { x: a.x, y: a.y, w: NOTE_SIZE, h: NOTE_SIZE }
  }
}

function distToSegment(p: Point, a: Point, b: Point) {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const len2 = dx * dx + dy * dy
  let t = len2 ? ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2 : 0
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

/** Precise hit test used by the eraser and click-selection. `tol` is in points. */
export function hitTest(a: Annotation, p: Point, tol = 4): boolean {
  switch (a.type) {
    case 'ink': {
      const pts = a.points
      const r = a.width / 2 + tol
      if (pts.length === 1) return Math.hypot(p.x - pts[0][0], p.y - pts[0][1]) <= r
      for (let i = 1; i < pts.length; i++) {
        if (distToSegment(p, { x: pts[i - 1][0], y: pts[i - 1][1] }, { x: pts[i][0], y: pts[i][1] }) <= r)
          return true
      }
      return false
    }
    case 'line':
    case 'arrow':
      return distToSegment(p, { x: a.x1, y: a.y1 }, { x: a.x2, y: a.y2 }) <= a.strokeWidth / 2 + tol
    case 'rect': {
      if (a.fill) return pointInRect(p, a, tol)
      const outer = pointInRect(p, a, tol + a.strokeWidth / 2)
      const inner = pointInRect(p, { x: a.x + tol, y: a.y + tol, w: a.w - tol * 2, h: a.h - tol * 2 })
      return outer && !(a.w > tol * 2 && a.h > tol * 2 && inner)
    }
    case 'ellipse': {
      const rx = a.w / 2
      const ry = a.h / 2
      const cx = a.x + rx
      const cy = a.y + ry
      const d = Math.hypot((p.x - cx) / Math.max(rx, 0.01), (p.y - cy) / Math.max(ry, 0.01))
      const band = (tol + a.strokeWidth / 2) / Math.max(Math.min(rx, ry), 1)
      return a.fill ? d <= 1 + band : Math.abs(d - 1) <= band
    }
    case 'highlight':
    case 'underline':
    case 'strikeout':
      return a.rects.some((r) => pointInRect(p, r, tol / 2))
    default:
      return pointInRect(p, annBBox(a), tol / 2)
  }
}

/** Map an annotation from one bounding box to another (used for move & resize). */
export function transformAnn<T extends Annotation>(a: T, from: Rect, to: Rect): T {
  const sx = from.w ? to.w / from.w : 1
  const sy = from.h ? to.h / from.h : 1
  const mx = (x: number) => to.x + (x - from.x) * sx
  const my = (y: number) => to.y + (y - from.y) * sy
  const now = Date.now()
  switch (a.type) {
    case 'ink':
      return { ...a, points: a.points.map(([x, y, p]) => [mx(x), my(y), p]), updatedAt: now }
    case 'line':
    case 'arrow':
      return { ...a, x1: mx(a.x1), y1: my(a.y1), x2: mx(a.x2), y2: my(a.y2), updatedAt: now }
    case 'highlight':
    case 'underline':
    case 'strikeout':
      return {
        ...a,
        rects: a.rects.map((r) => ({ x: mx(r.x), y: my(r.y), w: r.w * sx, h: r.h * sy })),
        updatedAt: now,
      }
    case 'note':
      return { ...a, x: mx(a.x), y: my(a.y), updatedAt: now }
    case 'text':
    case 'textEdit':
    case 'image':
    case 'rect':
    case 'ellipse': {
      // Bounding-box types are stored as the box itself (ignoring the ink pad).
      const box = { x: a.x, y: a.y, w: a.w, h: a.h }
      return {
        ...a,
        x: mx(box.x),
        y: my(box.y),
        w: Math.max(2, box.w * sx),
        h: Math.max(2, box.h * sy),
        updatedAt: now,
      }
    }
  }
  return a
}

export function translateAnn<T extends Annotation>(a: T, dx: number, dy: number): T {
  const b = annBBox(a)
  return transformAnn(a, b, { ...b, x: b.x + dx, y: b.y + dy })
}

/**
 * Convert a pointer position into page base-space points, given the bounding client rect of
 * the (possibly rotated) page element, the user rotation and the CSS scale.
 */
export function clientToPage(
  clientX: number,
  clientY: number,
  box: DOMRect,
  rotation: Rotation,
  scale: number,
  baseW: number,
  baseH: number,
): Point {
  const cx = box.left + box.width / 2
  const cy = box.top + box.height / 2
  const vx = clientX - cx
  const vy = clientY - cy
  const rad = (-rotation * Math.PI) / 180
  const ux = vx * Math.cos(rad) - vy * Math.sin(rad)
  const uy = vx * Math.sin(rad) + vy * Math.cos(rad)
  return { x: ux / scale + baseW / 2, y: uy / scale + baseH / 2 }
}

/** Size of a page on screen after the user's rotation, in points. */
export function rotatedSize(p: Pick<PageRef, 'width' | 'height' | 'rotation'>) {
  return p.rotation % 180 === 0 ? { w: p.width, h: p.height } : { w: p.height, h: p.width }
}

export const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v))
